/**
 * Media Session API（车载蓝牙/锁屏控制、音频焦点）
 * 抽离自 MusicPlayer.js
 */
import { useEffect, useCallback } from 'react';
import { getActiveMediaElement } from './utils';
import { getCoverImageUrl } from '../../utils/musicStreamUrls';

export function useMediaSession({
  currentMusic,
  playing,
  onNext,
  onPrevious,
  getCurrentLanguageTitle,
  getVideoFilePath,
  videoRef,
  audioRef,
  audioFocusMode,
  isMobileDevice
}) {
  const applyAudioFocusMode = useCallback((forceExclusive = false) => {
    if (!('mediaSession' in navigator)) return;
    try {
      const isMobile = isMobileDevice();
      const shouldExclusive = forceExclusive || audioFocusMode === 'exclusive';
      if (shouldExclusive) {
        const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
        navigator.mediaSession.setActionHandler('play', () => {
          const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
          if (mediaEl) mediaEl.play().catch(console.error);
        });
        if (playing) navigator.mediaSession.playbackState = 'playing';
        if (isMobile && playing) navigator.mediaSession.playbackState = 'playing';
      }
    } catch (_) {}
  }, [currentMusic, playing, audioFocusMode, getVideoFilePath, videoRef, audioRef, isMobileDevice]);
  const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (currentMusic) {
      const title = getCurrentLanguageTitle(currentMusic);
      const artist = currentMusic.artist || 'Unknown Artist';
      const album = currentMusic.album || '';
      const artwork = [];
      const cover = getCoverImageUrl(currentMusic);
      if (cover) {
        ['96x96', '128x128', '192x192', '256x256', '384x384', '512x512'].forEach(size => {
          artwork.push({ src: cover, sizes: size, type: 'image/webp' });
        });
      }
      navigator.mediaSession.metadata = new MediaMetadata({ title, artist, album, artwork });

      try {
        navigator.mediaSession.setActionHandler('play', () => {
          const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
          if (mediaEl) {
            mediaEl.play().catch(console.error);
            if (audioFocusMode === 'exclusive') applyAudioFocusMode(true);
          }
        });
        navigator.mediaSession.setActionHandler('pause', () => {
          const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
          if (mediaEl) mediaEl.pause();
        });
        navigator.mediaSession.setActionHandler('previoustrack', () => { if (onPrevious) onPrevious(); });
        navigator.mediaSession.setActionHandler('nexttrack', () => { if (onNext) onNext(); });
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
          if (mediaEl && details.seekTime !== undefined) mediaEl.currentTime = details.seekTime;
        });
        if (playing && audioFocusMode === 'exclusive') applyAudioFocusMode(true);
      } catch (error) {
        console.warn('Media Session actions warning:', error);
      }
    } else {
      navigator.mediaSession.metadata = null;
    }
  }, [currentMusic, onNext, onPrevious, playing, audioFocusMode, hasVideo, getCurrentLanguageTitle, getVideoFilePath, videoRef, audioRef, applyAudioFocusMode]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    if (playing) setTimeout(() => applyAudioFocusMode(true), 100);
  }, [playing, applyAudioFocusMode]);

  useEffect(() => {
    applyAudioFocusMode();
  }, [audioFocusMode, applyAudioFocusMode]);

  return { applyAudioFocusMode };
}
